import { AbstractTailoredWidgetTemplateEditor } from '@/components/template-widgets/AbstractTailoredWidgetEditorTemplate.class';
import { AdvizeoInputDirective } from '@/directives/advizeo-input/advizeo-input.directive';
import type { TemplateFormChangedEvent } from '@/interfaces/TailoredWidget.interface';
import { InformationProvider } from '@/providers/information/information.provider';
import type { RgbColorHex } from '@/shared/ui/theming';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiIconComponent } from '@/ui/icon/ui-icon';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { isNullish } from '@/utils/typing/is-nullish.predicate';
import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type CustomImageBase64Config = { image64: string; displayType: 'contain' | 'fill' | 'cover'; backgroundColor: RgbColorHex | null };

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./template-widget-custom-image-base64-editor.component.scss'],
    selector: 'template-widget-custom-image-base64-editor',
    templateUrl: 'template-widget-custom-image-base64-editor.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AdvizeoInputDirective,
        UiSelectComponent,
        UiFormFieldComponent,
        UiIconButtonComponent,
        UiIconComponent,
        ApplyPureFunctionPipe,
        TranslatePipe
    ]
})
export class TemplateWidgetCustomImageBase64EditorComponent extends AbstractTailoredWidgetTemplateEditor<CustomImageBase64Config> {
    private _informationProvider = inject(InformationProvider);
    private _formBuilder = inject(FormBuilder);
    private _translateService = inject(TranslateService);

    formInputs = input<CustomImageBase64Config>();
    config = output<TemplateFormChangedEvent>();

    IMAGE_MAX_SIZE = 20971520;
    IMAGE_MAX_HEIGHT = 15200;
    IMAGE_MAX_WIDTH = 25600;

    formGroup = this._formBuilder.group({
        image64: this._formBuilder.control<string | null>(this.formInputs()?.image64 ?? null, Validators.required),
        displayType: this._formBuilder.nonNullable.control<'contain' | 'fill' | 'cover'>(
            this.formInputs()?.displayType ?? 'contain',
            Validators.required
        ),
        backgroundColor: this._formBuilder.control<RgbColorHex | null>(this.formInputs()?.backgroundColor ?? null)
    });

    options = [
        { code: 'contain', label: this._translateService.instant('TAILORED_WIDGET.CUSTOM_IMAGE_BASE64.CONTAIN') },
        { code: 'fill', label: this._translateService.instant('TAILORED_WIDGET.CUSTOM_IMAGE_BASE64.FILL') },
        { code: 'cover', label: this._translateService.instant('TAILORED_WIDGET.CUSTOM_IMAGE_BASE64.COVER') }
    ];

    acceptedExtensions = ['jpeg', 'jpg', 'png'];
    acceptedExtensionsText = this.acceptedExtensions.map(extension => '.' + extension).join(',');

    constructor() {
        super();

        effect(() => {
            this.formGroup.patchValue({
                ...this.formInputs()
            });
        });

        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.formValueChanged());
    }

    imageChangeEvent(event: Event) {
        const fileInput = event.currentTarget as HTMLInputElement;
        if (fileInput.files && fileInput.files[0]) {
            const selectedFile = fileInput.files[0];

            if (selectedFile.size > this.IMAGE_MAX_SIZE) {
                console.error('Maximum size allowed is ' + this.IMAGE_MAX_SIZE / 1000 + 'Mb');
                this._informationProvider.showToast('ERRORS.FORM.ACTION.FAILED', 'error');
                return false;
            }

            const reader = new FileReader();
            reader.onload = (e: any) => {
                const image = new Image();
                image.src = e.target.result;
                image.onload = (rs: any) => {
                    const img_height = rs.currentTarget['height'];
                    const img_width = rs.currentTarget['width'];

                    if (img_height > this.IMAGE_MAX_HEIGHT && img_width > this.IMAGE_MAX_WIDTH) {
                        console.error('Maximum dimensions allowed ' + this.IMAGE_MAX_HEIGHT + '*' + this.IMAGE_MAX_WIDTH + 'px');
                        this._informationProvider.showToast('ERRORS.FORM.ACTION.FAILED', 'error');
                        return false;
                    } else {
                        const imgBase64Path = e.target.result;
                        const imgBase64 = {
                            name: selectedFile.name,
                            base64: imgBase64Path
                        };
                        this.formGroup.controls.image64.patchValue(JSON.stringify(imgBase64));
                        this.formGroup.controls.image64.markAsDirty({ onlySelf: true });
                    }
                };
            };

            reader.readAsDataURL(selectedFile);
        }
    }

    getDisplayedImageBase64Name = (imageBase64: string | null | undefined): string | null => {
        if (isNullish(imageBase64)) {
            return null;
        }
        const imageBase64Obj = JSON.parse(imageBase64);
        return imageBase64Obj.name;
    };

    formValueChanged() {
        const formChangedEvent: TemplateFormChangedEvent = {
            isValid: this.formGroup.valid,
            formValues: this.formGroup.value
        };
        this.config.emit(formChangedEvent);
    }

    triggerInput(inputId: string): void {
        document.getElementById(inputId)?.click();
    }

    clearBgColor(): void {
        this.formGroup.controls.backgroundColor.patchValue(null);
        this.formGroup.controls.backgroundColor.markAsDirty({ onlySelf: true });
    }
}
